/**
 * The stages an article passes through AFTER Mora hands it off, in order.
 *
 * ITC's real process once Mora has drafted: hand off, SEO and internal check (their SEO person), add
 * images, publish. `external_publications.state` carries one value from this list (plus `cancelled`,
 * which is an exit and never a stage). Every reader of that column (the status ladder, the progress
 * ring, the hand-off dialog, the pillar bars) takes its order and words from here so they cannot drift.
 */
export const PUBLICATION_STAGES = [
  'handed_off',
  'seo_review',
  'images',
  'scheduled',
  'published',
] as const;

export type PublicationStage = (typeof PUBLICATION_STAGES)[number];

/** The stages an operator can move an unpublished article between (published needs a URL, so it has its own route). */
export const PUBLICATION_WORKING_STAGES = [
  'handed_off',
  'seo_review',
  'images',
  'scheduled',
] as const;
export type PublicationWorkingStage = (typeof PUBLICATION_WORKING_STAGES)[number];

/** The word the Status column and its filter print. */
export const PUBLICATION_STATUS_WORD: Record<PublicationStage, string> = {
  handed_off: 'Handed off',
  seo_review: 'SEO review',
  images: 'Adding images',
  scheduled: 'Publish scheduled',
  published: 'Published',
};

/** The longer name the stage control and the progress ring use. */
export const PUBLICATION_STAGE_LABEL: Record<PublicationStage, string> = {
  handed_off: 'Handed off',
  seo_review: 'SEO and internal check',
  images: 'Adding images',
  scheduled: 'Publish scheduled',
  published: 'Published',
};

export function publicationStageIndex(state: PublicationStage): number {
  return PUBLICATION_STAGES.indexOf(state);
}

/** True when `state` is `stage` or any later one. */
export function publicationReached(state: PublicationStage, stage: PublicationStage): boolean {
  return publicationStageIndex(state) >= publicationStageIndex(stage);
}
